import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import api, { formatApiError } from "../../lib/api";
import { ROLE_LABELS, MODULE_LABELS } from "../../lib/rbac";
import { useAuth } from "../../context/AuthContext";
import { Navigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import { Plus, Loader2, Search, Pencil, ShieldCheck, ShieldOff, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

const ROLE_DEFAULT_MODULES = {
    admin: { purchasing: true, cash: true, hr: true },
    purchasing_manager: { purchasing: true, cash: false, hr: false },
    purchasing_specialist: { purchasing: true, cash: false, hr: false },
    warehouse: { purchasing: true, cash: false, hr: false },
    finance: { purchasing: true, cash: false, hr: false },
    project_manager: { purchasing: true, cash: false, hr: false },
    general_manager: { purchasing: true, cash: false, hr: false },
    auditor: { purchasing: true, cash: false, hr: false },
    requester: { purchasing: true, cash: false, hr: false },
    hr_officer: { purchasing: false, cash: false, hr: true },
};

const ROLE_OPTIONS = Object.keys(ROLE_DEFAULT_MODULES);

const emptyCreateForm = {
    name: "",
    email: "",
    password: "",
    role: "purchasing_specialist",
    department: "",
    modules: { purchasing: true, cash: false, hr: false },
};

export default function AdminUsers() {
    const { user: current } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [openCreate, setOpenCreate] = useState(false);
    const [createForm, setCreateForm] = useState(emptyCreateForm);
    const [busy, setBusy] = useState(false);

    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/users");
            setItems(r.data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (u) =>
                u.email.toLowerCase().includes(q) ||
                (u.name || "").toLowerCase().includes(q) ||
                (u.department || "").toLowerCase().includes(q) ||
                (ROLE_LABELS[u.role] || u.role || "").toLowerCase().includes(q)
        );
    }, [items, query]);

    // hard-guard: block non-admin (after all hooks)
    if (current && current.role !== "admin") {
        return <Navigate to="/portal" replace />;
    }

    const onCreate = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/admin/users", {
                email: createForm.email.trim(),
                password: createForm.password,
                name: createForm.name.trim(),
                role: createForm.role,
                department: createForm.department || null,
                modules: createForm.modules,
            });
            toast.success("Kullanıcı oluşturuldu");
            setOpenCreate(false);
            setCreateForm(emptyCreateForm);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const openEdit = (u) => {
        setEditUser(u);
        setEditForm({
            name: u.name || "",
            department: u.department || "",
            role: u.role,
            modules: {
                purchasing: !!u.modules?.purchasing,
                cash: !!u.modules?.cash,
                hr: !!u.modules?.hr,
            },
            active: u.active !== false,
            password: "",
        });
    };

    const onSaveEdit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const payload = {
                name: editForm.name.trim() || undefined,
                department: editForm.department || null,
                role: editForm.role,
                modules: editForm.modules,
                active: editForm.active,
            };
            if (editForm.password && editForm.password.length >= 6) {
                payload.password = editForm.password;
            }
            await api.patch(`/admin/users/${editUser.id}`, payload);
            toast.success("Kullanıcı güncellendi");
            setEditUser(null);
            setEditForm(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const toggleActive = async (u) => {
        try {
            await api.patch(`/admin/users/${u.id}`, { active: !u.active });
            toast.success(u.active ? "Kullanıcı pasif edildi" : "Kullanıcı aktif edildi");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const applyRolePreset = (role, target, setTarget) => {
        const defaults = ROLE_DEFAULT_MODULES[role] || { purchasing: false, cash: false, hr: false };
        setTarget({ ...target, role, modules: { ...defaults } });
    };

    return (
        <AppShell
            title="Kullanıcı Yönetimi"
            subtitle="Yönetim"
            actions={
                <Button data-testid="admin-users-new-btn" onClick={() => setOpenCreate(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Yeni Kullanıcı
                </Button>
            }
        >
            <div data-testid="admin-users-page" className="space-y-4">
                {/* Search bar */}
                <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-md">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            data-testid="admin-users-search"
                            placeholder="Ad, e-posta, rol veya departman ara..."
                            className="pl-9"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {filtered.length} / {items.length} kullanıcı
                    </div>
                </div>

                {/* Users table */}
                <div className="border rounded-lg overflow-hidden bg-card">
                    <div className="grid grid-cols-12 px-4 py-3 text-[11px] uppercase tracking-widest text-muted-foreground border-b bg-muted/30">
                        <div className="col-span-3">Kullanıcı</div>
                        <div className="col-span-2">Rol</div>
                        <div className="col-span-2">Departman</div>
                        <div className="col-span-3">Modüller</div>
                        <div className="col-span-1">Durum</div>
                        <div className="col-span-1 text-right">İşlem</div>
                    </div>
                    {loading ? (
                        <div className="p-10 flex justify-center">
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-10 text-center text-sm text-muted-foreground">
                            <UsersIcon className="w-8 h-8 mx-auto mb-3 opacity-40" />
                            Kayıt bulunamadı
                        </div>
                    ) : (
                        filtered.map((u) => (
                            <div
                                key={u.id}
                                data-testid={`user-row-${u.email}`}
                                className="grid grid-cols-12 px-4 py-3 items-center border-b last:border-b-0 hover:bg-muted/20"
                            >
                                <div className="col-span-3 min-w-0">
                                    <div className="font-medium text-sm truncate">{u.name}</div>
                                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                                </div>
                                <div className="col-span-2 text-sm">
                                    <Badge variant="secondary" className="text-xs font-normal">
                                        {ROLE_LABELS[u.role] || u.role}
                                    </Badge>
                                </div>
                                <div className="col-span-2 text-sm text-muted-foreground truncate">
                                    {u.department || "—"}
                                </div>
                                <div className="col-span-3 flex flex-wrap gap-1">
                                    {Object.entries(MODULE_LABELS).map(([k, l]) =>
                                        u.modules?.[k] ? (
                                            <span key={k} className="text-[10px] uppercase tracking-wider bg-primary/10 text-primary rounded px-1.5 py-0.5">
                                                {l}
                                            </span>
                                        ) : null
                                    )}
                                </div>
                                <div className="col-span-1">
                                    {u.active ? (
                                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                                            <ShieldCheck className="w-3 h-3" /> Aktif
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                            <ShieldOff className="w-3 h-3" /> Pasif
                                        </span>
                                    )}
                                </div>
                                <div className="col-span-1 flex items-center justify-end gap-2">
                                    <Button
                                        data-testid={`edit-user-${u.email}`}
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => openEdit(u)}
                                    >
                                        <Pencil className="w-3.5 h-3.5" />
                                    </Button>
                                    {u.id !== current?.id && (
                                        <Button
                                            data-testid={`toggle-active-${u.email}`}
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => toggleActive(u)}
                                            title={u.active ? "Pasif Yap" : "Aktif Yap"}
                                        >
                                            {u.active
                                                ? <ShieldOff className="w-3.5 h-3.5" />
                                                : <ShieldCheck className="w-3.5 h-3.5" />}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Create dialog */}
            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Yeni Kullanıcı</DialogTitle>
                        <DialogDescription>
                            Kullanıcı bilgilerini, rolünü ve modül yetkilerini belirleyin.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={onCreate} className="space-y-4" data-testid="create-user-form">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Ad Soyad</Label>
                                <Input
                                    data-testid="create-user-name"
                                    required
                                    value={createForm.name}
                                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label className="text-xs">E-posta</Label>
                                <Input
                                    data-testid="create-user-email"
                                    type="email"
                                    required
                                    value={createForm.email}
                                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs">Şifre (en az 6)</Label>
                                <Input
                                    data-testid="create-user-password"
                                    type="password"
                                    required
                                    minLength={6}
                                    value={createForm.password}
                                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Departman</Label>
                                <Input
                                    data-testid="create-user-department"
                                    value={createForm.department}
                                    onChange={(e) => setCreateForm({ ...createForm, department: e.target.value })}
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Rol</Label>
                            <Select
                                value={createForm.role}
                                onValueChange={(v) => applyRolePreset(v, createForm, setCreateForm)}
                            >
                                <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {ROLE_OPTIONS.map((r) => (
                                        <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <div className="text-[11px] text-muted-foreground mt-1">
                                Rol seçilince modül yetkileri varsayılan olarak dolduruldu. Aşağıdan tek tek düzenleyebilirsiniz.
                            </div>
                        </div>
                        <ModulesEditor
                            modules={createForm.modules}
                            onChange={(m) => setCreateForm({ ...createForm, modules: m })}
                            testidPrefix="create"
                        />
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpenCreate(false)} disabled={busy}>
                                İptal
                            </Button>
                            <Button data-testid="create-user-submit" type="submit" disabled={busy}>
                                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Oluştur
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit dialog */}
            <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) { setEditUser(null); setEditForm(null); } }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Kullanıcıyı Düzenle</DialogTitle>
                        <DialogDescription>
                            {editUser?.email} — rol, modül ve durum bilgilerini yönetin.
                        </DialogDescription>
                    </DialogHeader>
                    {editForm && (
                        <form onSubmit={onSaveEdit} className="space-y-4" data-testid="edit-user-form">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs">Ad Soyad</Label>
                                    <Input
                                        data-testid="edit-user-name"
                                        value={editForm.name}
                                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Departman</Label>
                                    <Input
                                        data-testid="edit-user-department"
                                        value={editForm.department}
                                        onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs">Rol</Label>
                                <Select
                                    value={editForm.role}
                                    onValueChange={(v) => applyRolePreset(v, editForm, setEditForm)}
                                    disabled={editUser?.id === current?.id}
                                >
                                    <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {ROLE_OPTIONS.map((r) => (
                                            <SelectItem key={r} value={r}>{ROLE_LABELS[r] || r}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {editUser?.id === current?.id && (
                                    <div className="text-[11px] text-amber-600 mt-1">
                                        Kendi rolünüzü değiştiremezsiniz.
                                    </div>
                                )}
                            </div>
                            <ModulesEditor
                                modules={editForm.modules}
                                onChange={(m) => setEditForm({ ...editForm, modules: m })}
                                testidPrefix="edit"
                            />
                            <div className="flex items-center justify-between border rounded-md px-3 py-2">
                                <div>
                                    <div className="text-sm font-medium">Durum</div>
                                    <div className="text-xs text-muted-foreground">Pasif kullanıcı giriş yapamaz.</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs">{editForm.active ? "Aktif" : "Pasif"}</span>
                                    <Switch
                                        data-testid="edit-user-active"
                                        checked={editForm.active}
                                        onCheckedChange={(v) => setEditForm({ ...editForm, active: v })}
                                        disabled={editUser?.id === current?.id}
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs">Yeni Şifre (opsiyonel, en az 6)</Label>
                                <Input
                                    data-testid="edit-user-password"
                                    type="password"
                                    placeholder="Boş bırakılırsa değişmez"
                                    value={editForm.password}
                                    onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                                />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => { setEditUser(null); setEditForm(null); }} disabled={busy}>
                                    İptal
                                </Button>
                                <Button data-testid="edit-user-submit" type="submit" disabled={busy}>
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                    Kaydet
                                </Button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}

function ModulesEditor({ modules, onChange, testidPrefix }) {
    return (
        <div className="border rounded-md p-3 space-y-2">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
                Modül Erişimleri
            </div>
            {Object.entries(MODULE_LABELS).map(([k, label]) => (
                <div key={k} className="flex items-center justify-between py-1.5">
                    <div className="text-sm">{label}</div>
                    <Switch
                        data-testid={`${testidPrefix}-module-${k}`}
                        checked={!!modules[k]}
                        onCheckedChange={(v) => onChange({ ...modules, [k]: v })}
                    />
                </div>
            ))}
        </div>
    );
}
